// Carrier metadata shared by the API (adapters registry, credential validation) and the web app (Intégrations >
// Livraison, ship modal). Facts come from docs/research/CARRIERS-TN.md (decision D18). Capabilities describe what
// the carrier API really offers; anything not documented is declared false. The platform adds its own features on
// top for every carrier: generated A6 labels, bulk shipping (sequential, paced), manual statuses.

export const CARRIER_CODES = [
  "first_delivery",
  "intigo",
  "navex",
  "colissimo",
  "aramex",
  "dhl_express",
  "mylerz",
  "jax",
  "adex",
  "bigboss",
  "axess",
  "manual",
] as const;
export type CarrierCode = (typeof CARRIER_CODES)[number];

export const CARRIER_CAPABILITIES = ["create", "bulk", "track", "cancel", "label", "pickup", "localities", "cod"] as const;
export type CarrierCapability = (typeof CARRIER_CAPABILITIES)[number];
export type CarrierCapabilities = Record<CarrierCapability, boolean>;

export const CARRIER_CAPABILITY_LABELS: Record<CarrierCapability, string> = {
  create: "Création de colis",
  bulk: "Création groupée",
  track: "Suivi automatique",
  cancel: "Annulation",
  label: "Étiquette transporteur",
  pickup: "Demande d'enlèvement",
  localities: "Référentiel de localités",
  cod: "Paiement à la livraison",
};

export type CarrierDocsQuality = "official" | "official_partial" | "unofficial" | "none";

export const CARRIER_DOCS_QUALITY_LABELS: Record<CarrierDocsQuality, string> = {
  official: "Documentation officielle",
  official_partial: "Documentation officielle partielle",
  unofficial: "Sources non officielles",
  none: "Sans API",
};

export interface CarrierCredentialField {
  name: string;
  label: string;
  help: string;
  secret: boolean;
  type: "text" | "password" | "email";
  required: boolean;
  placeholder?: string;
  defaultValue?: string;
  minLength?: number;
  maxLength?: number;
}

export interface CarrierMeta {
  code: CarrierCode;
  name: string;
  website: string | null;
  /** Brand-like color of the monogram tile. */
  color: string;
  monogram: string;
  markets: string[];
  /** false for the manual carrier: no network call is ever made. */
  api: boolean;
  credentialFields: CarrierCredentialField[];
  /** Where the merchant finds the credentials (French). */
  credentialsHelp: string;
  capabilities: CarrierCapabilities;
  docsQuality: CarrierDocsQuality;
  /** Limits and gotchas shown on the carrier card (French). */
  note: string;
  /** required: the destination must be chosen in the carrier catalog synced per connection. */
  localityMode: "none" | "required";
  /** Highest amount the carrier accepts to collect, in TND millimes (null = not documented). */
  maxCodMinor: number | null;
  cancelNote?: string;
  pickupNote?: string;
}

const NONE: CarrierCapabilities = { create: false, bulk: false, track: false, cancel: false, label: false, pickup: false, localities: false, cod: false };
const caps = (on: CarrierCapability[]): CarrierCapabilities => ({ ...NONE, ...Object.fromEntries(on.map((c) => [c, true])) });

const secret = (name: string, label: string, help: string, extra: Partial<CarrierCredentialField> = {}): CarrierCredentialField => ({
  name,
  label,
  help,
  secret: true,
  type: "password",
  required: true,
  minLength: 4,
  maxLength: 4096,
  ...extra,
});

const text = (name: string, label: string, help: string, extra: Partial<CarrierCredentialField> = {}): CarrierCredentialField => ({
  name,
  label,
  help,
  secret: false,
  type: "text",
  required: true,
  minLength: 1,
  maxLength: 256,
  ...extra,
});

export const CARRIERS: Record<CarrierCode, CarrierMeta> = {
  first_delivery: {
    code: "first_delivery",
    name: "First Delivery",
    website: "https://www.firstdeliverygroup.com",
    color: "#e5482b",
    monogram: "FD",
    markets: ["TN"],
    api: true,
    credentialFields: [secret("token", "Jeton API", "Jeton Bearer fourni par First Delivery (API v2).", { minLength: 8 })],
    credentialsHelp: "Demandez le jeton API v2 à votre chargé de compte First Delivery ou au support. Il n'existe pas d'écran libre service.",
    capabilities: caps(["create", "bulk", "track", "cancel", "label", "pickup", "localities", "cod"]),
    docsQuality: "official",
    note: "Chaque colis doit être rattaché à une localité First Delivery : synchronisez le référentiel puis choisissez la localité quand l'adresse est ambiguë. Suivi limité à 1 requête par seconde.",
    localityMode: "required",
    maxCodMinor: 999_000,
    cancelNote: "Annulation possible uniquement tant que le colis est en attente.",
  },
  intigo: {
    code: "intigo",
    name: "Intigo",
    website: "https://www.intigo.net",
    color: "#6d28d9",
    monogram: "IN",
    markets: ["TN"],
    api: true,
    credentialFields: [secret("apiKey", "Clé API", "Clé X-API-Key de votre compte partenaire Intigo.", { minLength: 8 })],
    credentialsHelp: "Dans votre espace partenaire my.intigo.net (compte validé), ouvrez l'administration et copiez la clé API.",
    capabilities: caps(["create", "bulk", "track", "cancel", "label", "cod"]),
    docsQuality: "official",
    note: "La création d'un colis confirme automatiquement l'enlèvement : c'est un engagement, pas un brouillon. Paiement à la livraison plafonné à 1 000 DT sauf autorisation Intigo.",
    localityMode: "none",
    maxCodMinor: 5_000_000,
    cancelNote: "Avant enlèvement le colis est annulé ; après enlèvement l'annulation devient un retour expéditeur.",
    pickupNote: "Enlèvement confirmé automatiquement à la création du colis.",
  },
  navex: {
    code: "navex",
    name: "Navex",
    website: "https://navex.tn",
    color: "#1f5fbf",
    monogram: "NX",
    markets: ["TN"],
    api: true,
    credentialFields: [
      secret("createToken", "Token de création", "Affiché sur la page de documentation de création de votre espace expéditeur Navex.", { minLength: 8 }),
      secret("trackToken", "Token de récupération (token_etat)", "Affiché sur la page de documentation du suivi. Il est différent du token de création.", { minLength: 8 }),
    ],
    credentialsHelp: "Connectez-vous à app.navex.tn/expediteur puis ouvrez les pages de documentation API : chaque page affiche votre token. Copiez le token de création et le token de récupération.",
    capabilities: caps(["create", "track", "cancel", "cod"]),
    docsQuality: "official_partial",
    note: "Navex ne fournit ni étiquette ni enlèvement par API : DigiSubs génère l'étiquette A6 avec code-barres. Le token de création est validé à la première expédition.",
    localityMode: "none",
    maxCodMinor: null,
    cancelNote: "Suppression non documentée par Navex : elle est confirmée par une vérification du suivi.",
  },
  colissimo: {
    code: "colissimo",
    name: "Colissimo",
    website: "https://colissimo.com.tn",
    color: "#b7791f",
    monogram: "CO",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("username", "Utilisateur API", "Identifiant API au format clt_...", { placeholder: "clt_...", minLength: 3 }),
      secret("password", "Mot de passe API (Pass)", "Mot de passe généré avec l'identifiant API.", { minLength: 1 }),
    ],
    credentialsHelp: "Sur delivery.colissimo.com.tn, menu du profil puis Profil : en bas de page, cliquez sur Utiliser l'API et générer les paramètres d'accès.",
    capabilities: caps(["create", "track", "cancel", "label", "localities", "cod"]),
    docsQuality: "official",
    note: "Gouvernorat et ville doivent venir du référentiel Colissimo synchronisé. Le service est injoignable tant que Colissimo n'a pas renouvelé son certificat TLS expiré.",
    localityMode: "required",
    maxCodMinor: null,
    cancelNote: "Suppression possible uniquement tant que le colis est en attente.",
  },
  aramex: {
    code: "aramex",
    name: "Aramex",
    website: "https://www.aramex.com/tn/en",
    color: "#d71f26",
    monogram: "AR",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("userName", "E-mail du compte", "Adresse e-mail de connexion aramex.com.", { type: "email", minLength: 5 }),
      secret("password", "Mot de passe", "Mot de passe de l'API Shipping Services.", { minLength: 1 }),
      text("accountNumber", "Numéro de compte", "Numéro de compte client Aramex.", { minLength: 3, maxLength: 32 }),
      secret("accountPin", "Code PIN du compte", "PIN fourni avec les accès API.", { minLength: 3, maxLength: 32 }),
      text("accountEntity", "Entité", "Entité Aramex du compte (TUN pour la Tunisie).", { defaultValue: "TUN", minLength: 3, maxLength: 3 }),
      text("accountCountryCode", "Pays du compte", "Code pays ISO du compte (TN).", { defaultValue: "TN", minLength: 2, maxLength: 2 }),
    ],
    credentialsHelp: "Demandez l'accès à l'API Shipping Services à votre commercial Aramex : il fournit le numéro de compte, l'identifiant, le mot de passe, le PIN et l'entité.",
    capabilities: caps(["create", "bulk", "track", "label", "pickup", "localities", "cod"]),
    docsQuality: "official_partial",
    note: "La ville du destinataire et celle de l'enlèvement doivent suivre l'orthographe Aramex (référentiel synchronisé). Aramex ne permet pas d'annuler une expédition par API.",
    localityMode: "required",
    maxCodMinor: null,
    cancelNote: "Aramex ne permet pas d'annuler une expédition : contactez votre agence Aramex.",
  },
  dhl_express: {
    code: "dhl_express",
    name: "DHL Express",
    website: "https://mydhl.express.dhl/tn/fr",
    color: "#d40511",
    monogram: "DH",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("apiKey", "Clé API (utilisateur)", "Identifiant MyDHL API fourni par DHL.", { minLength: 3 }),
      secret("apiSecret", "Secret API (mot de passe)", "Mot de passe MyDHL API.", { minLength: 3 }),
      text("accountNumber", "Numéro de compte expéditeur", "Numéro de compte DHL Express.", { minLength: 6, maxLength: 12 }),
    ],
    credentialsHelp: "Depuis le portail développeur DHL (MyDHL API), demandez l'accès : un conseiller DHL Express valide puis fournit la clé et le secret de production.",
    capabilities: caps(["create", "track", "label", "pickup"]),
    docsQuality: "official",
    note: "Service express international : aucun paiement à la livraison n'est proposé en Tunisie. L'étiquette est conservée à la création. Aucune annulation d'expédition possible.",
    localityMode: "none",
    maxCodMinor: 0,
    cancelNote: "DHL Express ne permet pas d'annuler une expédition, seulement un enlèvement.",
  },
  mylerz: {
    code: "mylerz",
    name: "Mylerz",
    website: "https://www.mylerz.com",
    color: "#f26a21",
    monogram: "MY",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("username", "Identifiant", "Identifiant de votre compte Mylerz Tunisie.", { minLength: 2 }),
      secret("password", "Mot de passe", "Mot de passe du compte Mylerz.", { minLength: 1 }),
    ],
    credentialsHelp: "Utilisez l'identifiant et le mot de passe de votre compte marchand Mylerz Tunisie (création sur demande à Mylerz).",
    capabilities: caps(["create", "bulk", "track", "cod"]),
    docsQuality: "official_partial",
    note: "Le serveur Mylerz Tunisie refuse les connexions non autorisées : demandez à Mylerz d'autoriser l'adresse IP de votre serveur. Étiquette, annulation et enlèvement ne sont pas documentés.",
    localityMode: "none",
    maxCodMinor: null,
  },
  jax: {
    code: "jax",
    name: "Jax Delivery",
    website: "https://app.jax-delivery.com",
    color: "#0f766e",
    monogram: "JX",
    markets: ["TN"],
    api: true,
    credentialFields: [secret("token", "Token API", "Token longue durée fourni par Jax Delivery.", { minLength: 8 })],
    credentialsHelp: "Jax ne publie pas de documentation : demandez à Jax un token API longue durée pour votre compte.",
    capabilities: caps(["create", "track", "localities", "cod"]),
    docsQuality: "unofficial",
    note: "API non documentée publiquement : création et suivi reposent sur l'application officielle observée. Les libellés de statut inconnus restent en évidence sans changer l'état du colis.",
    localityMode: "required",
    maxCodMinor: null,
  },
  adex: {
    code: "adex",
    name: "Adex",
    website: "https://adex.tn",
    color: "#2563eb",
    monogram: "AD",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("login", "Identifiant", "Identifiant de votre espace my.adex.tn.", { minLength: 2 }),
      secret("password", "Mot de passe", "Mot de passe de votre espace Adex.", { minLength: 1 }),
    ],
    credentialsHelp: "Utilisez l'identifiant et le mot de passe de votre espace expéditeur my.adex.tn.",
    capabilities: caps(["track"]),
    docsQuality: "unofficial",
    note: "Aucune création de colis par API n'a été trouvée : créez le colis dans votre espace Adex puis saisissez le code-barres dans DigiSubs. Le suivi lit la liste de vos colis.",
    localityMode: "none",
    maxCodMinor: null,
  },
  bigboss: {
    code: "bigboss",
    name: "BigBoss Express",
    website: "https://my.bigbossexpress.tn",
    color: "#15803d",
    monogram: "BB",
    markets: ["TN"],
    api: true,
    credentialFields: [
      text("login", "Identifiant", "Identifiant de votre espace my.bigbossexpress.tn.", { minLength: 2 }),
      secret("password", "Mot de passe", "Mot de passe de votre espace BigBoss Express.", { minLength: 1 }),
    ],
    credentialsHelp: "Utilisez l'identifiant et le mot de passe de votre espace expéditeur my.bigbossexpress.tn.",
    capabilities: caps(["track"]),
    docsQuality: "unofficial",
    note: "Aucune création de colis par API n'a été trouvée : créez le colis dans votre espace BigBoss puis saisissez le code-barres dans DigiSubs. Le suivi lit la liste de vos colis.",
    localityMode: "none",
    maxCodMinor: null,
  },
  axess: {
    code: "axess",
    name: "Axess Logistique",
    website: "https://www.axesslogistique.com",
    color: "#0e7490",
    monogram: "AX",
    markets: ["TN"],
    api: true,
    credentialFields: [secret("token", "Token API", "Token de votre espace client Axess Logistique.", { minLength: 8 })],
    credentialsHelp: "Le token se trouve dans l'espace client Axess Logistique (page API, accessible après connexion).",
    capabilities: caps(["create", "track", "label", "localities", "cod"]),
    docsQuality: "unofficial",
    note: "Gouvernorat, délégation et localité doivent venir du référentiel Axess (synchronisé). Annulation non documentée. Les statuts non publiés restent en évidence.",
    localityMode: "required",
    maxCodMinor: null,
  },
  manual: {
    code: "manual",
    name: "Transporteur sans API",
    website: null,
    color: "#475569",
    monogram: "MA",
    markets: ["TN", "DZ", "MA", "LY", "EG", "FR", "BE", "SN", "CI", "SA", "AE", "QA", "CA"],
    api: false,
    credentialFields: [],
    credentialsHelp: "Aucun identifiant : donnez simplement un nom (La Poste, livreur indépendant...).",
    capabilities: caps(["create", "cod"]),
    docsQuality: "none",
    note: "Pour toute société sans API (La Poste, livreur local) : numéros de suivi saisis à la main, statuts mis à jour manuellement, étiquette A6 générée par DigiSubs.",
    localityMode: "none",
    maxCodMinor: null,
  },
};

export const CARRIER_LIST: CarrierMeta[] = CARRIER_CODES.map((code) => CARRIERS[code]);

export function isCarrierCode(value: unknown): value is CarrierCode {
  return typeof value === "string" && (CARRIER_CODES as readonly string[]).includes(value);
}

export function carrierMeta(code: string | null | undefined): CarrierMeta | null {
  return isCarrierCode(code) ? CARRIERS[code] : null;
}

export function carrierName(code: string | null | undefined): string {
  return carrierMeta(code)?.name ?? (code || "Transporteur");
}
